import React, { useState } from "react";
import {
  TrendingUp,
  TrendingDown,
  Minus,
  AlertTriangle,
  CheckCircle2,
  Info,
  Zap,
  Compass,
  Clock,
  Target,
  History,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

export default function MultiTimeframeMatrix({
  data,
  currentSymbol,
  currentTimeframe,
  onSelectTimeframe,
}) {
  const [showHistory, setShowHistory] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    try {
      return localStorage.getItem("minopro_mtf_collapsed") === "true";
    } catch (e) {
      return false;
    }
  });

  const toggleCollapse = () => {
    setIsCollapsed(prev => {
      const next = !prev;
      try { localStorage.setItem("minopro_mtf_collapsed", String(next)); } catch (e) {}
      return next;
    });
  };

  const matrix = data?.matrix || [];
  const consensus = data?.consensus || "ĐA KHUNG ĐANG PHÂN HÓA";
  const consensusType = data?.consensus_type || "MIXED";
  const bullCount = data?.bullish_count || 0;
  const bearCount = data?.bearish_count || 0;
  const neutralCount = data?.neutral_count || 0;
  const totalTf = data?.total_tf || 6;
  const confluenceScore = data?.confluence_score || Math.round((Math.max(bullCount, bearCount) / totalTf) * 100);

  const warningType = data?.warning_type || "NEUTRAL";
  const counterTrendWarning = data?.counter_trend_warning || "";
  const actionableAdvice = data?.actionable_advice || "";

  const adviceTimeStr = data?.advice_time_str;
  const adviceDateStr = data?.advice_date_str;
  const adviceRefPrice = data?.advice_ref_price;
  const adviceHistory = data?.advice_history || [];

  return (
    <div className="bg-slate-900/80 border border-slate-800/90 rounded-2xl p-3 sm:p-4 flex flex-col gap-3 shadow-2xl backdrop-blur-md">
      {/* 1. Header Bar with Confluence Meter & Consensus Badge */}
      <div className={`flex flex-wrap items-center justify-between gap-2.5 sm:gap-3 ${isCollapsed ? "" : "border-b border-slate-800/80 pb-3"}`}>
        <div className="flex items-center gap-2 sm:gap-2.5">
          <div className="w-7 h-7 rounded-lg bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center text-cyan-400 shadow-sm shrink-0">
            <Compass className="w-4 h-4" />
          </div>
          <div>
            <div className="flex items-center gap-1.5 sm:gap-2">
              <h3 className="font-bold text-xs uppercase tracking-wider text-slate-100 flex items-center gap-1">
                Chỉ Huy Đa Khung
              </h3>
              <span className="text-[10px] font-mono text-cyan-400 bg-cyan-950/60 border border-cyan-800/60 px-1.5 py-0.2 rounded font-semibold">
                {currentSymbol}
              </span>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2 text-[10.5px] sm:text-[11px] text-slate-400 mt-0.5">
              <span>Đồng thuận: <strong className="text-slate-200 font-mono">{confluenceScore}%</strong></span>
              <span>•</span>
              <span className="text-emerald-400 font-mono font-semibold">{bullCount} Tăng</span>
              <span>/</span>
              <span className="text-rose-400 font-mono font-semibold">{bearCount} Giảm</span>
              {neutralCount > 0 && (
                <>
                  <span>/</span>
                  <span className="text-slate-400 font-mono">{neutralCount} Ngang</span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Consensus Badge & History Toggle */}
        <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
          {!isCollapsed && adviceHistory.length > 0 && (
            <button
              onClick={() => setShowHistory(!showHistory)}
              className={`px-2 sm:px-2.5 py-1 rounded-lg border text-xs font-semibold flex items-center gap-1 transition-all cursor-pointer ${
                showHistory
                  ? "bg-amber-500/20 text-amber-300 border-amber-500/50 shadow-sm shadow-amber-950/40"
                  : "bg-slate-800/80 text-slate-300 hover:text-white border-slate-700/80 hover:bg-slate-700/60"
              }`}
              title="Xem lịch sử các nhận định trước đó"
            >
              <History className="w-3.5 h-3.5 text-amber-400" />
              <span className="hidden sm:inline">Lịch Sử Nhận Định</span>
              <span className="sm:hidden">Lịch Sử</span>
              {showHistory ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
          )}

          <span
            className={`text-xs font-bold px-2.5 sm:px-3 py-1 rounded-lg border font-mono tracking-wide shadow-sm flex items-center gap-1.5 ${
              consensusType === "STRONG_BULL"
                ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/40 shadow-emerald-950/30"
                : consensusType === "STRONG_BEAR"
                ? "bg-rose-500/20 text-rose-300 border-rose-500/40 shadow-rose-950/30"
                : consensusType === "BULL"
                ? "bg-emerald-900/30 text-emerald-400 border-emerald-700/40"
                : consensusType === "BEAR"
                ? "bg-rose-900/30 text-rose-400 border-rose-700/40"
                : "bg-slate-800 text-amber-300 border-slate-700"
            }`}
          >
            {consensusType === "STRONG_BULL" && <Zap className="w-3.5 h-3.5 text-emerald-400" />}
            {consensusType === "STRONG_BEAR" && <Zap className="w-3.5 h-3.5 text-rose-400" />}
            {consensus}
          </span>

          {/* Collapse / Expand Toggle */}
          <button
            onClick={toggleCollapse}
            className="px-2 py-1 rounded-lg bg-slate-800/90 hover:bg-slate-700 text-slate-300 hover:text-cyan-300 border border-slate-700/80 transition-all cursor-pointer flex items-center gap-1 text-xs font-semibold shadow-sm"
            title={isCollapsed ? "Mở rộng Chỉ Huy Đa Khung" : "Thu gọn Chỉ Huy Đa Khung"}
          >
            <span className="text-[11px] hidden sm:inline">{isCollapsed ? "Mở rộng" : "Thu gọn"}</span>
            {isCollapsed ? <ChevronDown className="w-3.5 h-3.5 text-cyan-400" /> : <ChevronUp className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>

      {!isCollapsed && (
        <>

      {/* 2. Strategic Guidance Banner with Timestamp & Ref Price */}
      {counterTrendWarning && (
        <div
          className={`px-3 sm:px-3.5 py-2.5 rounded-xl border flex flex-col gap-2 text-xs transition-all ${
            warningType === "WARNING"
              ? "bg-rose-950/25 border-rose-800/40 text-rose-200"
              : warningType === "CONFLUENCE"
              ? "bg-emerald-950/25 border-emerald-800/40 text-emerald-200"
              : "bg-slate-950/40 border-slate-800/70 text-slate-300"
          }`}
        >
          <div className="flex items-start gap-2.5">
            <div className="mt-0.5 shrink-0">
              {warningType === "WARNING" && <AlertTriangle className="w-4 h-4 text-amber-400" />}
              {warningType === "CONFLUENCE" && <CheckCircle2 className="w-4 h-4 text-emerald-400" />}
              {warningType === "NEUTRAL" && <Info className="w-4 h-4 text-cyan-400" />}
            </div>
            <div className="flex flex-col gap-1 flex-1">
              <div className="font-semibold text-[11.5px] leading-snug">
                {counterTrendWarning}
              </div>
              {actionableAdvice && (
                <div className="text-[11px] text-slate-300/90 leading-relaxed font-sans">
                  👉 {actionableAdvice}
                </div>
              )}
            </div>
          </div>

          {/* Meta Bar: Issuance Timestamp & Reference Price */}
          <div className="flex flex-wrap items-center justify-between gap-1.5 sm:gap-2 pt-1.5 border-t border-slate-800/50 text-[10px] sm:text-[10.5px] text-slate-400">
            <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
              <span className="flex items-center gap-1 text-slate-300 font-mono">
                <Clock className="w-3 h-3 text-cyan-400 shrink-0" />
                <span className="hidden sm:inline">Thời gian phát: </span>
                <strong className="text-amber-300">{adviceTimeStr || "Mới nhất"}</strong>
                <span className="text-slate-500 text-[9.5px]">({adviceDateStr || "Hôm nay"})</span>
              </span>
              <span className="text-slate-600">•</span>
              <span className="flex items-center gap-1 text-slate-300 font-mono">
                <Target className="w-3 h-3 text-emerald-400 shrink-0" />
                <span className="hidden sm:inline">Giá lúc nhận định: </span>
                <strong className="text-slate-100">{adviceRefPrice ? Number(adviceRefPrice).toFixed(2) : "---"}</strong>
              </span>
            </div>
            <div className="text-[9.5px] sm:text-[10px] text-slate-400 font-mono">
              <span className="hidden sm:inline">Khung chuẩn: </span>
              <span className="text-cyan-400 font-semibold">M15 (Chiến lược)</span>
            </div>
          </div>
        </div>
      )}

      {/* 3. Lịch Sử Nhận Định Drawer */}
      {showHistory && adviceHistory.length > 0 && (
        <div className="bg-slate-950/70 border border-slate-800/90 rounded-xl p-3 flex flex-col gap-2.5 animate-fadeIn text-xs">
          <div className="flex items-center justify-between border-b border-slate-800/70 pb-2">
            <div className="flex items-center gap-1.5 text-amber-300 font-bold text-[11.5px]">
              <History className="w-3.5 h-3.5" />
              <span>Nhật Ký Bản Tin & Lịch Sử Nhận Định ({currentSymbol} — Khung Chuẩn M15)</span>
            </div>
            <span className="text-[10px] text-slate-400 font-mono">
              Lưu trữ các mốc thay đổi định hướng thị trường
            </span>
          </div>

          <div className="overflow-x-auto max-h-56 overflow-y-auto">
            <table className="w-full text-left border-collapse text-xs">
              <thead>
                <tr className="text-[10px] text-slate-400 border-b border-slate-800/60 uppercase font-mono">
                  <th className="py-1.5 px-2 font-semibold whitespace-nowrap">Thời Gian</th>
                  <th className="py-1.5 px-2 font-semibold whitespace-nowrap">Định Hướng</th>
                  <th className="py-1.5 px-2 font-semibold whitespace-nowrap">Giá Lúc Nhận Định</th>
                  <th className="py-1.5 px-2 font-semibold">Nội Dung Nhận Định & Vùng Cản M15</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/40">
                {adviceHistory.map((item, idx) => {
                  const isSell = item.action === "Canh BÁN" || item.action === "CANH_BAN";
                  const isBuy = item.action === "Canh MUA" || item.action === "CANH_MUA";

                  return (
                    <tr key={item.id || idx} className="hover:bg-slate-900/50 transition">
                      <td className="py-2 px-2 font-mono text-[10.5px] text-slate-300 whitespace-nowrap">
                        <span className="text-amber-300 font-semibold">{item.time_str}</span>{" "}
                        <span className="text-slate-500 text-[9.5px]">({item.date_str})</span>
                      </td>
                      <td className="py-2 px-2 whitespace-nowrap">
                        <span
                          className={`px-2 py-0.5 rounded font-bold font-mono text-[10px] ${
                            isSell
                              ? "bg-rose-500/20 text-rose-300 border border-rose-500/30"
                              : isBuy
                              ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30"
                              : "bg-slate-800 text-slate-300 border border-slate-700"
                          }`}
                        >
                          {isSell ? "Canh BÁN" : isBuy ? "Canh MUA" : "Quan Sát"}
                        </span>
                      </td>
                      <td className="py-2 px-2 font-mono text-[11px] text-slate-100 font-semibold whitespace-nowrap">
                        {item.ref_price ? Number(item.ref_price).toFixed(2) : "---"}
                      </td>
                      <td className="py-2 px-2 text-[11px] text-slate-300">
                        <div className="font-semibold text-slate-200 leading-tight">
                          {item.headline || item.action_label || ""}
                        </div>
                        {item.advice && (
                          <div className="text-[10.5px] text-slate-400 mt-0.5 leading-relaxed">
                            👉 {item.advice}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 4. Interactive 6-Timeframe Grid Cards (M1 — D1) */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-2.5">
        {matrix.map((item, idx) => {
          const isSelected = item.timeframe === currentTimeframe;
          const isBull = item.bias === "BULLISH";
          const isBear = item.bias === "BEARISH";
          const isLastSingle = matrix.length % 2 === 1 && idx === matrix.length - 1;

          // Structure styling
          const struct = item.structure || "RANGE";
          const structLabel =
            struct === "BOS_BULL"
              ? "Phá Đỉnh ↗"
              : struct === "BOS_BEAR"
              ? "Phá Đáy ↘"
              : struct === "CHOCH_BULL"
              ? "Đảo Tăng ⚡"
              : struct === "CHOCH_BEAR"
              ? "Đảo Giảm ⚡"
              : struct.includes("PULLBACK")
              ? "Sóng Hồi 🔄"
              : "Tích Lũy ⏸";

          const structColor =
            struct === "BOS_BULL" || struct === "CHOCH_BULL"
              ? "text-emerald-400 bg-emerald-500/10 border-emerald-500/20"
              : struct === "BOS_BEAR" || struct === "CHOCH_BEAR"
              ? "text-rose-400 bg-rose-500/10 border-rose-500/20"
              : "text-slate-400 bg-slate-800/40 border-slate-700/40";

          // Momentum (RSI) indicator
          const rsiVal = Math.round(item.rsi || 50);
          const rsiLabel =
            rsiVal <= 30
              ? `Quá Bán (${rsiVal}) ⚠️`
              : rsiVal >= 70
              ? `Quá Mua (${rsiVal}) ⚠️`
              : `RSI: ${rsiVal}`;

          const rsiColor =
            rsiVal <= 30
              ? "text-amber-400 font-semibold"
              : rsiVal >= 70
              ? "text-purple-400 font-semibold"
              : rsiVal >= 55
              ? "text-emerald-400"
              : rsiVal <= 45
              ? "text-rose-400"
              : "text-slate-400";

          return (
            <button
              key={item.timeframe}
              onClick={() => onSelectTimeframe(item.timeframe)}
              className={`p-2 sm:p-2.5 rounded-xl border transition-all flex flex-col justify-between gap-1.5 sm:gap-2 text-left cursor-pointer group relative overflow-hidden ${
                isLastSingle ? "col-span-2 sm:col-span-1" : ""
              } ${
                isSelected
                  ? "bg-gradient-to-b from-slate-800/95 to-slate-900 border-amber-400/90 shadow-lg shadow-amber-950/20 ring-1 ring-amber-400/50 scale-[1.01]"
                  : "bg-slate-950/50 hover:bg-slate-900/80 border-slate-800/80 hover:border-slate-700 hover:scale-[1.01]"
              }`}
            >
              {/* Active Indicator Pip */}
              {isSelected && (
                <div className="absolute top-1 right-1 w-2 h-2 rounded-full bg-amber-400 animate-pulse shadow-sm" />
              )}

              {/* Timeframe & Bias Header */}
              <div className="flex items-center justify-between">
                <span className={`text-xs font-black font-mono tracking-wide ${isSelected ? "text-amber-400" : "text-slate-200"}`}>
                  {item.timeframe}
                </span>

                <div
                  className={`px-1.5 py-0.5 rounded text-[9.5px] font-bold font-mono flex items-center gap-1 border whitespace-nowrap ${
                    isBull
                      ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30"
                      : isBear
                      ? "bg-rose-500/15 text-rose-400 border-rose-500/30"
                      : "bg-slate-800 text-slate-400 border-slate-700"
                  }`}
                >
                  {isBull ? (
                    <TrendingUp className="w-2.5 h-2.5" />
                  ) : isBear ? (
                    <TrendingDown className="w-2.5 h-2.5" />
                  ) : (
                    <Minus className="w-2.5 h-2.5" />
                  )}
                  <span>{isBull ? "TĂNG" : isBear ? "GIẢM" : "NGANG"}</span>
                </div>
              </div>

              {/* Structure Tag */}
              <div className="flex items-center justify-between text-[10px] gap-1">
                <span className="text-slate-500 text-[9px] sm:text-[9.5px] shrink-0 whitespace-nowrap font-sans">Cấu trúc:</span>
                <span className={`px-1.5 py-0.2 rounded border font-mono text-[9px] font-semibold whitespace-nowrap ${structColor}`}>
                  {structLabel}
                </span>
              </div>

              {/* Momentum / RSI Footer */}
              <div className="pt-1 border-t border-slate-800/60 flex items-center justify-between text-[10px] gap-1">
                <span className="text-slate-500 text-[9px] sm:text-[9.5px] shrink-0 whitespace-nowrap font-sans">Động lượng:</span>
                <span className={`font-mono text-[9px] sm:text-[9.5px] whitespace-nowrap ${rsiColor}`}>
                  {rsiLabel}
                </span>
              </div>
            </button>
          );
        })}
      </div>
        </>
      )}
    </div>
  );
}